<template>
  <MarketingLayout>
    <header class="page-head">
      <p class="eyebrow">SUPPORT</p>
      <h1>How can we <em>help?</em></h1>
      <p class="lead">Answers to the questions we hear most from WinCard businesses. Can't find what you need? <router-link to="/contact">Contact our team</router-link>.</p>
    </header>

    <section class="cat-grid">
      <article v-for="cat in categories" :key="cat.title" class="cat-card">
        <span class="cat-icon">{{ cat.icon }}</span>
        <h3>{{ cat.title }}</h3>
        <ul>
          <li v-for="topic in cat.topics" :key="topic">{{ topic }}</li>
        </ul>
      </article>
    </section>

    <section class="faq-section">
      <h2>Frequently asked questions</h2>
      <div class="faq-list">
        <article v-for="(item, index) in faqs" :key="item.q" :class="{ expanded: openFaq === index }">
          <button @click="openFaq = openFaq === index ? null : index"><span>{{ item.q }}</span><b>{{ openFaq === index ? '−' : '+' }}</b></button>
          <p v-if="openFaq === index">{{ item.a }}</p>
        </article>
      </div>
    </section>

    <section class="cta-strip">
      <div>
        <h3>Still need a hand?</h3>
        <p>Our team typically replies within one business day.</p>
      </div>
      <router-link class="button" to="/contact">Contact support <span>→</span></router-link>
    </section>
  </MarketingLayout>
</template>

<script>
import MarketingLayout from '@/components/MarketingLayout.vue'
import { useSeo } from '@/composables/useSeo'

export default {
  name: 'HelpCenter',
  components: { MarketingLayout },
  setup() {
    useSeo({
      title: 'Help center',
      description: 'Answers to common questions about setting up WinCard, billing, wallet cards, and troubleshooting.',
      path: '/help-center',
    });
  },
  data() {
    return {
      openFaq: 0,
      categories: [
        { icon: '✦', title: 'Getting started', topics: ['Creating your first wallet card', 'Inviting your team', 'Setting up your first reward rule'] },
        { icon: '$', title: 'Billing & plans', topics: ['Changing your plan', 'Updating payment details', 'Understanding your invoice'] },
        { icon: 'w', title: 'Wallet cards', topics: ['Apple Wallet vs Google Wallet', 'Customizing card design', 'Why a card isn’t updating'] },
        { icon: '!', title: 'Troubleshooting', topics: ['QR code not scanning', 'Customer not receiving rewards', 'Login and access issues'] },
      ],
      faqs: [
        { q: 'How do I reset a customer’s visit count?', a: 'From your dashboard, open the customer’s profile and select "Adjust visits." Changes apply to their wallet card within a few seconds.' },
        { q: 'Can I change reward rules after launch?', a: 'Yes. Reward rule changes apply going forward and never affect progress customers have already earned.' },
        { q: 'Why isn’t my QR code scanning?', a: 'Make sure the code is printed at least 2x2cm and isn’t behind glass or a glare-heavy surface. You can regenerate a fresh code anytime from Settings.' },
        { q: 'How do I add a teammate to my account?', a: 'Go to Settings → Team and send an invite by email. Teammates can manage campaigns and rewards but can’t change billing.' },
        { q: 'Who do I contact for billing questions?', a: 'Reach out through our contact form and mention your business name, our billing team will follow up directly.' },
      ],
    }
  },
}
</script>

<style scoped>
.page-head { max-width: 620px; margin: 0 auto 60px; text-align: center; }
.page-head .eyebrow { justify-content: center; }
.page-head h1 { font-size: 52px; letter-spacing: -2.6px; line-height: .98; margin: 0 0 18px; font-weight: 600; }
.page-head h1 em { font-family: var(--serif); font-weight: 400; }
.page-head .lead { margin: 0 auto; }
.page-head .lead a { color: var(--green); font-weight: 600; }

.cat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 90px; }
.cat-card { border: 1px solid #e3e8e4; background: #fff; border-radius: 10px; padding: 24px; }
.cat-icon { width: 32px; height: 32px; display: grid; place-items: center; border-radius: 8px; background: var(--lime); color: var(--ink); font-weight: 700; margin-bottom: 16px; }
.cat-card h3 { font-size: 15px; letter-spacing: -.4px; margin: 0 0 10px; }
.cat-card ul { list-style: none; margin: 0; padding: 0; }
.cat-card li { font-size: 12px; color: var(--muted); line-height: 1.8; }

.faq-section h2 { font-size: 36px; letter-spacing: -1.8px; margin: 0 0 20px; }

.cta-strip { margin-top: 70px; background: var(--cream); border: 1px solid #ebefeb; border-radius: 12px; padding: 34px 40px; display: flex; align-items: center; justify-content: space-between; gap: 20px; }
.cta-strip h3 { margin: 0 0 4px; font-size: 19px; }
.cta-strip p { margin: 0; color: var(--muted); font-size: 13px; }
.cta-strip .button { white-space: nowrap; }

@media (max-width: 900px) { .cat-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 650px) { .cat-grid { grid-template-columns: 1fr; } .page-head h1 { font-size: 40px; } .cta-strip { flex-direction: column; align-items: flex-start; } }
</style>
